import { useState, useRef, useEffect } from "react";
import Image from "@Components/Image";
import CountryFlag from "@Components/CountryFlag";
import styles from "@Pages/VideoPage/VideoPageMobile.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";
import useTranslationHook from "hooks/useTranslationHook";
import { getRandomAvatar } from "@Pages/PropertyPage/components/ReviewsSectionV2/avatars";
import {
  ANONYMOUS_REVIEWER_KEY,
  isAnonymousReview,
} from "@Pages/PropertyPage/components/ReviewsSectionV2/anonymousReviewer";
import { getNationalityCountryCode } from "@Pages/PropertyPage/components/ReviewsSectionV2/utils";

interface MobileVideoReviewCardProps {
  review: any;
}

const formatReviewCreatedAt = (createdAt: string): string => {
  const legacy = new Date(createdAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return config.IS_CHINA_APP ? renderChinaNamedDate(createdAt, legacy) : legacy;
};

const MobileVideoReviewCard = ({ review }: MobileVideoReviewCardProps) => {
  const { t } = useTranslationHook();
  const [showFull, setShowFull] = useState<boolean>(false);
  const reviewCardTextRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState<boolean>(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (reviewCardTextRef.current) {
        setHasOverflow(
          reviewCardTextRef.current.scrollHeight > reviewCardTextRef.current.clientHeight,
        );
      }
    };

    checkOverflow(); // Check on mount

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [review]); // Re-run if text changes

  const handleToggleReview = () => {
    setShowFull((val) => !val);
  };

  const anonymous = isAnonymousReview(review);
  const name = anonymous
    ? t(ANONYMOUS_REVIEWER_KEY)
    : review?.user_details?.name || review?.name || "";
  const text = review?.content || review?.review || review?.text || "";
  const rating = review?.rating;
  const profilePic = anonymous
    ? ""
    : review?.user_details?.profile_pic || review?.user_details?.avatar || "";
  const nationality =
    review?.booking_details?.nationality || review?.user_details?.nationality || "";
  const countryCode = getNationalityCountryCode(nationality);

  const charLimit = 100;
  const isLong = text.length > charLimit;
  const displayedContent = !isLong || showFull ? text : `${text.slice(0, charLimit)}..`;

  return (
    <div
      className={formatClassNames(
        styles.reviewCardMobile,
        hasOverflow && styles.reviewCardMobileNoBottomPadding,
      )}
    >
      <div className={styles.reviewCardMobileTop}>
        <div className={styles.reviewCardMobileInfo}>
          <div className={styles.reviewCardMobileAvatar}>
            {profilePic ? (
              <Image
                src={profilePic}
                alt={name}
                width={32}
                height={32}
                className={styles.reviewCardMobileAvatarImg}
              />
            ) : (
              <Image
                src={getRandomAvatar(name, 0)}
                alt={name}
                width={32}
                height={32}
                className={styles.reviewCardMobileAvatarImg}
              />
            )}
            {countryCode && (
              <div className={styles.reviewCardMobileFlag}>
                <CountryFlag countryCode={countryCode} width={16} height={16} />
              </div>
            )}
          </div>
          <div className={styles.reviewCardMobileUserDetails}>
            <div className={styles.reviewCardMobileName}>{name}</div>
            <div className={styles.reviewCardMobileDate}>
              {review?.created_at ? formatReviewCreatedAt(review.created_at) : ""}
            </div>
          </div>
        </div>
        {rating && (
          <div className={styles.reviewCardMobileRating}>
            {parseFloat(rating).toFixed(1)}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        )}
      </div>
      {text && (
        <div ref={reviewCardTextRef} className={styles.reviewCardMobileText}>
          {displayedContent}
          {isLong && (
            <span
              role="button"
              tabIndex={0}
              className={styles.reviewCardMobileReadMore}
              onClick={handleToggleReview}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleToggleReview();
              }}
            >
              {" "}
              {/* Expand/collapse toggle */}
              {showFull
                ? t("propertypage.reviewssection.readless")
                : t("propertypage.reviewssection.readmore")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(MobileVideoReviewCard, {
  componentName: "MobileVideoReviewCard-ReviewsSection-PropertyPage",
  showForChina: true,
});
